import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import { DBTimestampSchema } from '../common/sql';
import { DBTaxonomyKeySchema, DBTopicStatusSchema } from '../topics/schemas';
import { TaxonomyStatus } from '../topics/enums';

// String bounds match the tables' CHECK constraints. A tighter bound would log a
// warning for rows the database accepts.

/** One tree of topic groups, such as a syllabus or a concept hierarchy. */
export const DBTopicGroupingSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  title: AllCharLabelSchema.min(1),
  subjectId: UUIDSchema.nullable(),
  sourceFile: AllCharLabelSchema.nullable(),
  sourceVersion: AllCharLabelSchema.nullable(),
  mappingSemantics: AllCharLabelSchema.nullable(),
  sourceCodeStatus: AllCharLabelSchema.nullable(),
  status: DBTopicStatusSchema,
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBTopicGroupSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  groupingId: UUIDSchema,
  nodeTypeId: UUIDSchema,
  name: AllCharLabelSchema.min(1),
  description: AllCharLabelSchema.nullable(),
  code: AllCharLabelSchema.nullable(),
  ref: AllCharLabelSchema.nullable(),
  note: AllCharLabelSchema.nullable(),
  inquiryQuestion: AllCharLabelSchema.nullable(),
  reviewNote: AllCharLabelSchema.nullable(),
  requirementRelationId: UUIDSchema.nullable(),
  contextRef: AllCharLabelSchema.nullable(),
  sourceItemIndex: z.number().int().positive().nullable(),
  sourceFile: AllCharLabelSchema.nullable(),
  sourceRequirementIndex: z.number().int().positive().nullable(),
  sourcePage: z.number().int().positive().nullable(),
  sourceParagraphIndex: z.number().int().min(0).nullable(),
  sourceYear: AllCharLabelSchema.nullable(),
  status: DBTopicStatusSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/** Each group has one parent. Only topics can sit under more than one group. */
export const DBTopicGroupChildSchema = z.object({
  parentGroupId: UUIDSchema,
  childGroupId: UUIDSchema,
  position: z.number().int(),
  createdAt: DBTimestampSchema,
});

/** A topic can sit under several groups, such as a syllabus group and a concept group. */
export const DBTopicGroupTopicSchema = z.object({
  groupId: UUIDSchema,
  topicId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

export const DBOutcomeTopicAssociationSchema = z.object({
  groupingId: UUIDSchema,
  outcomeCode: z.string().min(1),
  topicId: UUIDSchema,
  associationTypeId: UUIDSchema,
  note: AllCharLabelSchema.nullable(),
  createdAt: DBTimestampSchema,
});

/**
 * Client Schemas
 */

export const TopicGroupingTitleSchema = AllCharLabelSchema.min(1).openapi({
  description: 'What the tree is called',
  example: 'Physics — Stage 6 (2017)',
});

export const TopicGroupingSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  title: TopicGroupingTitleSchema,
  subjectId: UUIDSchema.nullable(),
  sourceFile: AllCharLabelSchema.nullable(),
  sourceVersion: AllCharLabelSchema.nullable(),
  mappingSemantics: AllCharLabelSchema.nullable(),
  sourceCodeStatus: AllCharLabelSchema.nullable(),
  status: z.enum(TaxonomyStatus),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicGroupNameSchema = AllCharLabelSchema.min(1).openapi({
  description: 'What the node is called',
  example: 'Projectile motion',
});

export const TopicGroupSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  groupingId: UUIDSchema,
  nodeTypeId: UUIDSchema,
  name: TopicGroupNameSchema,
  description: AllCharLabelSchema.nullable(),
  code: AllCharLabelSchema.nullable(),
  ref: AllCharLabelSchema.nullable(),
  note: AllCharLabelSchema.nullable(),
  inquiryQuestion: AllCharLabelSchema.nullable(),
  reviewNote: AllCharLabelSchema.nullable(),
  requirementRelationId: UUIDSchema.nullable(),
  contextRef: AllCharLabelSchema.nullable(),
  sourceItemIndex: z.number().int().positive().nullable(),
  sourceFile: AllCharLabelSchema.nullable(),
  sourceRequirementIndex: z.number().int().positive().nullable(),
  sourcePage: z.number().int().positive().nullable(),
  sourceParagraphIndex: z.number().int().min(0).nullable(),
  sourceYear: AllCharLabelSchema.nullable(),
  status: z.enum(TaxonomyStatus),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicGroupChildSchema = z.object({
  parentGroupId: UUIDSchema,
  childGroupId: UUIDSchema,
  position: z.number().int(),
  createdAt: UnixTimestampSchema,
});

export const TopicGroupTopicSchema = z.object({
  groupId: UUIDSchema,
  topicId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const OutcomeTopicAssociationSchema = z.object({
  groupingId: UUIDSchema,
  outcomeCode: z.string().min(1),
  topicId: UUIDSchema,
  associationTypeId: UUIDSchema,
  note: AllCharLabelSchema.nullable(),
  createdAt: UnixTimestampSchema,
});
